import React, { useEffect, useState } from "react";
import AITrustSignalLogoBlue from "/AITrustSignalLogoBlue.svg";
import { X } from "lucide-react";
import { useReportsStore } from "@/store/store";
import { IProgressReport } from "@/types/report";
import { useNavigate } from "react-router-dom";
import { useAnimatedProgress } from "@/hooks/useAnimatedProgress";
import { computeFakeProgressPercent } from "@/lib/progressUtils";

const MobileGeneratePreviewItem = ({
  reportId,
  report,
  onReportClick,
}: {
  reportId: number;
  report: IProgressReport;
  onReportClick: (report: IProgressReport) => void;
}) => {
  const navigate = useNavigate();
  const [currentProgress, setCurrentProgress] = useState<number>(0);
  const [signalNames, setSignalNames] = useState<string[]>([]);
  const [completedSignalsCounter, setCompletedSignalsCounter] = useState(0);

  const finalProgress = useAnimatedProgress(
    report.created_ts,
    currentProgress,
    report?.status === "in_progress" || report?.status === "finalizing"
  );

  useEffect(() => {
    if (report?.signals) {
      let completedSignals = 0;
      const names: string[] = [];

      report.signals.forEach((signal) => {
        names.push(signal.name);
        if (signal.status === "completed") {
          completedSignals++;
        }
      });

      setSignalNames(names);
      // Same derivation the GenerateDialog uses, so the bar and the dialog it
      // launches never disagree about how far along the same report is.
      const percentage = computeFakeProgressPercent(report);
      setCompletedSignalsCounter(completedSignals);
      setCurrentProgress(percentage);
    }
  }, [report]);

  const handleClick = () => {
    if (report?.status === "completed" && report?.company_id) {
      navigate(`/reports/${report.company_id}/${reportId}`);
    } else if (report?.status === "in_progress" || report?.status === "finalizing") {
      onReportClick(report);
    }
  };

  return (
    <div className="mb-3 rounded-t-vb-lg border border-b-0 border-vb-line bg-vb-surface shadow-vb-xl">
      <div className="cursor-pointer rounded-t-vb-lg" onClick={handleClick}>
        <div className="flex flex-col items-center gap-3 px-4 py-4">
          <div className="flex w-full items-center gap-3">
            <div
              className="h-[53px] w-[120px] shrink-0 bg-contain bg-center bg-no-repeat"
              style={{ backgroundImage: `url('${AITrustSignalLogoBlue}')` }}
              role="img"
              aria-label="AI Trust Signal"
            />
            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-[13.5px] font-bold text-vb-text">
                {report?.status === "completed"
                  ? `Your report for ${report.company_name} is ready!`
                  : "Our reports take time to run"}
              </span>
              <span className="text-[12px] font-medium text-vb-text-2">
                {report?.status === "completed" ? (
                  <span className="text-[13px] font-bold text-vb-brand">
                    Check the report
                  </span>
                ) : (
                    report?.status === "in_progress" ? signalNames[completedSignalsCounter] : "Finalizing"
                )}
              </span>
            </div>
          </div>

          {report?.status !== "completed" && (
            <div className="w-full">
              <div className="mb-2 flex w-full justify-between text-[12px] font-medium text-vb-text-3">
                <span>Generating report</span>
                <span>{Math.round(finalProgress)}%</span>
              </div>
              <div className="h-1 w-full overflow-hidden rounded-vb-pill bg-vb-bg-subtle">
                <div
                  className="h-full rounded-vb-pill bg-vb-brand transition-all duration-300 ease-out"
                  style={{ width: `${finalProgress}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MobileGeneratePreview = ({
  onReportClick,
}: {
  onReportClick: (report: IProgressReport) => void;
}) => {
  const generatingReports = useReportsStore((s) => s.generatingReports);
  // Dismissed per report id. The Home screen used to own a "hide the bar" flag
  // (AITS-540 deleted it), and without a replacement this pinned bar became
  // undismissable on every screen rather than just one. Reappears for a
  // genuinely new build, since the key is the report id.
  const [dismissed, setDismissed] = useState<Record<string, true>>({});

  // The store now also retains completed reports (so the desktop GenerateDialog
  // can observe completion); keep the mobile bar showing only still-generating
  // ones, as before.
  const validReports = Object.entries(generatingReports).filter(
    ([reportId, report]) =>
      report.status !== "error" && report.status !== "completed" && !dismissed[reportId]
  );

  if (validReports.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-30">
      <div className="max-h-[80vh] overflow-y-auto">
        {validReports.map(([reportId, report]) => (
          <div key={reportId} className="relative">
            <button
              type="button"
              aria-label="Dismiss report progress"
              onClick={() => setDismissed((d) => ({ ...d, [reportId]: true }))}
              className="absolute right-2 top-2 z-10 grid h-7 w-7 place-items-center rounded-vb-pill text-vb-text-3 hover:bg-vb-bg-subtle"
            >
              <X className="h-4 w-4" />
            </button>
            <MobileGeneratePreviewItem
              reportId={Number(reportId)}
              report={report}
              onReportClick={onReportClick}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default MobileGeneratePreview;
